import React, { ReactNode } from 'react';
import { FetchableContainer, useFetchable } from './useFetchable';
import { RefetchBlock } from '../../components/molecules/RefetchBlock';
import { isHidden } from '../../lib/BlockUtils/BlockUtils';
import { isDynamicallyGeneratedBlockContent, isFetchError } from '../../types/api/Block';
import { LoadingSpinner } from '../../components/atoms/LoadingSpinner';
import bem from '../../lib/bemUtils/bem';
import { DynamicallyGeneratedContentHint } from '../components/Hint';
import { StatusInfoErrorWithRequestId } from '../components/StatusInfo';
import { FormResponseErrorType } from '../../components/molecules/Form';
import { StatusInfoModifier } from '../components/StatusInfo/partials/StatusInfo';

export type RefreshCallback<RefreshProps = never> = [RefreshProps] extends [never]
  ? () => void
  : (refreshProps: RefreshProps) => void;

export type ContainerRenderer<Embedded> = {
  embedded: Embedded;
  refresh: RefreshCallback;
  refreshCount: number;
  refreshProps: any;
};

type FetchedContainerProps<Embedded, Container extends FetchableContainer<Embedded>> = {
  children(props: ContainerRenderer<Embedded>): ReactNode;
  fallbackResponseWhenResponseIsNullOrUndefined?: Embedded;
} & Container;

export function FetchedContainer<Embedded, Container extends FetchableContainer<Embedded>>({
  children,
  ...container
}: FetchedContainerProps<Embedded, Container>) {
  const props = container as unknown as Container;
  const { embedded, loading, refresh, refreshCount, refreshProps, requestError } = useFetchable<
    Embedded,
    FetchableContainer<Embedded>
  >(props as unknown as Container);

  if (requestError) {
    // session/csrf errors are not recoverable => do not show the refresh button
    return (
      <RefetchBlock
        errorTextTranslationKey={requestError}
        refetchBlock={requestError === FormResponseErrorType.EXPIRED_SESSION_OR_CSRF ? undefined : refresh}
      />
    );
  }
  if (isHidden(props) && !embedded) {
    return null;
  }

  if (embedded && isDynamicallyGeneratedBlockContent(embedded)) {
    return <DynamicallyGeneratedContentHint />;
  }

  if (embedded && isFetchError(embedded)) {
    return (
      <StatusInfoErrorWithRequestId
        modifier={StatusInfoModifier.CENTER}
        key={props.fetchUrl}
        requestId={embedded.requestId}
      />
    );
  }
  if (embedded || loading) {
    return (
      <div className={bem('block-fetcher', loading ? 'loading' : undefined)}>
        {loading && (
          <div className={bem('block-fetcher__spinner', embedded && 'overlap')}>
            <div className="block-fetcher__spinner-container">
              <LoadingSpinner />
            </div>
          </div>
        )}
        {embedded && children({ embedded, refresh, refreshCount, refreshProps })}
      </div>
    );
  }
  return null;
}
